<script setup>
import LogoutTime from '@/layouts/components/LogoutTime.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'

definePage({
  meta: {
    layout: 'blank',
    action: 'write',
    subject: 'project',
    navActiveLink: 'projects',
  },
})

import { phase, projectStatus, type } from '@/datas/projects'
import { useSnackBar } from '@/utils/stores/snackbar'
import { computed, reactive, ref } from 'vue' // ✅ computed 추가
import { useRouter } from 'vue-router'

const router = useRouter()
const snackbarStore = useSnackBar()

const data = reactive({
  projectName: "",
  projectSummary: "",
  projectNo: "",
  projectDesc: "",
  targetCount: "",
  sponsor: "",
  type: "",
  typeEtc: "",           // ✅ 기타일 때 자유입력
  phase: "",
  phaseEtc: "",          // ✅ phase=0일 때 자유입력
  projectStatus: "",     // ✅ 누락되면 라디오 바인딩 경고 나올 수 있어 기본 추가
  startDate: "",
  isNewProject: true,
  hasRandomization: true,
  hasGuardianSurvey: true,
  hasRtms: false,
  projectColor: "gray",
})

const colorOptions = [ 
  { text: '회색', value: 'gray', color: '#F2F4F6' },
  { text: '빨강', value: 'red', color: '#ED9797' },
  { text: '주황', value: 'orange', color: '#E5AB91' },
  { text: '노랑', value: 'yellow', color: '#E4C8A0' },
  { text: '초록', value: 'green', color: '#34AA5C' },
  { text: '파랑', value: 'blue', color: '#7FABDD' },
  { text: '보라', value: 'purple', color: '#C3A3E5' },
]


const refForm = ref(null)

const typeRadios = type
const phaseRadios = phase
const projectStatusRadios = projectStatus

// ✅ '기타' 선택 여부
const isTypeEtc = computed(() => {
  const v = String(data.type ?? '').trim()
  
  return v === '기타' || v.toLowerCase() === 'etc'
})

// ✅ phase가 0(기타)인지 여부
const isPhaseEtc = computed(() => {
  const phase = data.phase
  const value = phase === "" ? -1 : Number(phase ?? 0)
  
  return value === 0
})


const saveProject = async () => {
  const validate = await refForm?.value.validate()
  if (!validate.valid) return

  // 기타 입력값 사용 조건에 맞춰 정리
  const typeEtcValue  = isTypeEtc.value ? (data.typeEtc || '') : null
  const phaseEtcValue = isPhaseEtc.value ? (data.phaseEtc || '') : null

  // 날짜 정규화(YYYY-MM-DD), 비어있으면 null
  const startDateStr = data.startDate
    ? new Date(data.startDate).toISOString().slice(0, 10)
    : null

  const payload = {
    projectName: data.projectName,
    projectSummary: data.projectSummary,
    projectNo: data.projectNo,
    projectDesc: data.projectDesc,
    sponsor: data.sponsor,
    type: data.type,
    typeEtc: typeEtcValue,                  // ✅ 같이 전송
    phase: Number(data.phase),
    phaseEtc: phaseEtcValue,                // ✅ 같이 전송
    projectStatus: data.projectStatus,

    targetCount: data.targetCount,
    startDate: startDateStr,
    hasRandomization: !!data.hasRandomization,
    hasGuardianSurvey: !!data.hasGuardianSurvey,
    hasRtms: !!data.hasRtms,
    projectColor: data.projectColor,
    isNewProject: !!data.isNewProject,
  }

  await $api(`/projects`, { method: 'POST', body: { project: payload } })
  snackbarStore.showSnackBar("Successfully Saved!")
  router.push({ name: `projects` })
}
</script>

<template>
  <VCard
    class="topbar-card"
    style="border-radius:0;"
  >
    <VCardText class="d-flex justify-space-between align-between py-2">
      <!-- 👇 로고 버튼 (왼쪽) -->
      <div
        class="d-flex align-center cursor-pointer"
        @click="router.push('/projects')"
      >
        <img
          src="/images/home_logo.png"
          alt="홈"
          width="87"
          height="30"
          style="display:block"
        >
      </div>

      <!-- 오른쪽 기존 요소 -->
      <div class="d-flex align-center">
        <LogoutTime class="me-2" />
        <UserProfile />
        <div class="divider-vert" />
        <div
          class="mgmt-quick d-flex align-center px-2 py-1"
          :style="{ backgroundColor: hover ? 'rgba(0,0,0,0.05)' : 'transparent' }"
          @click="router.push('/manage/permissions')"
          @mouseover="hover = true"
          @mouseleave="hover = false"
        >
          <img
            src="/images/settings.png"
            alt="관리"
            class="icon-20 me-1"
          >
          <span class="text-gray-600">관리</span>
        </div>
      </div>
    </VCardText>
  </VCard>

  <VRow class="mx-10 ">
    <VCol
      class="mx-auto"
      style="margin-block-start: 50px;"
      cols="12"
      sm="12"
      md="9"
      lg="7"
      order="1"
      order-lg="1"
    >
      <VCard
        title="시험 등록"
        class="form-card"
      >
        <VCardText class="mt-4">
          <VForm
            ref="refForm"
            @submit.prevent="() => {}"
          >
            <VRow>
              <!-- 프로젝트 요약 -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectSummary"
                    >
                      프로젝트 명<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectSummary"
                      v-model="data.projectSummary"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Project Name -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectName"
                    >
                      {{ $t('Project Name') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectName"
                      v-model="data.projectName"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Project No -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectNo"
                    >
                      {{ $t('Project No') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectNo"
                      v-model="data.projectNo"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Project Desc -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectDesc"
                    >
                      {{ $t('Project Description') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectDesc"
                      v-model="data.projectDesc"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Sponsor -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="sponsor"
                    >
                      {{ $t('Sponsor') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="sponsor"
                      v-model="data.sponsor"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Type + 기타 입력 -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="type"
                    >
                      {{ $t('Type') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VRadioGroup v-model="data.type">
                      <VRadio
                        v-for="(item, index) in typeRadios"
                        :key="index"
                        :value="item.value"
                        :label="item.text"
                        density="compact"
                      />
                    </VRadioGroup>

                    <!-- 기타일 때만 노출 -->
                    <AppTextField
                      v-if="isTypeEtc"
                      v-model="data.typeEtc"
                      placeholder="기타 유형을 입력하세요"
                      :rules="[requiredValidator]"
                      hide-details="auto"
                      density="comfortable"
                      class="mt-2"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Phase + 기타 입력(값 0) -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="phase"
                    >
                      {{ $t('Phase') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VRadioGroup v-model="data.phase">
                      <VRadio
                        v-for="(item, index) in phaseRadios"
                        :key="index"
                        :value="item.value"
                        :label="item.text"
                        density="compact"
                      />
                    </VRadioGroup>

                    <!-- phase=0(기타)일 때만 노출 -->
                    <AppTextField
                      v-if="isPhaseEtc"
                      v-model="data.phaseEtc"
                      placeholder="기타 Phase를 입력하세요"
                      :rules="[requiredValidator]"
                      hide-details="auto"
                      density="comfortable"
                      class="mt-2"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Project Status -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="project_status"
                    >
                      {{ $t('ProjectStatus') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VRadioGroup v-model="data.projectStatus">
                      <VRadio
                        v-for="(item, index) in projectStatusRadios"
                        :key="index"
                        :value="item.value"
                        :label="item.text"
                        density="compact"
                      />
                    </VRadioGroup>
                  </VCol>
                </VRow>
              </VCol>

              <!-- 토글들 -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label class="v-label text-body-2 text-high-emphasis">무작위 배정 여부</label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VSwitch
                      v-model="data.hasRandomization"
                      inset
                      :label="data.hasRandomization ? '사용' : '미사용'"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label class="v-label text-body-2 text-high-emphasis">보호자 설문 여부</label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VSwitch
                      v-model="data.hasGuardianSurvey"
                      inset
                      :label="data.hasGuardianSurvey ? '사용' : '미사용'"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label class="v-label text-body-2 text-high-emphasis">시험약 관리 여부</label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VSwitch
                      v-model="data.hasRtms"
                      inset
                      :label="data.hasRtms ? '사용' : '미사용'"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Project Color -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="6"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="projectColor"
                    >프로젝트 컬러</label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VSelect
                      v-model="data.projectColor"
                      :items="colorOptions"
                      item-title="text"
                      item-value="value"
                      density="comfortable"
                      hide-details
                    >
                      <template #item="{ props, item }">
                        <VListItem v-bind="props">
                          <template #prepend>
                            <div
                              :style="{
                                backgroundColor: item.raw.color,
                                width: '16px',
                                height: '16px',
                                borderRadius: '4px',
                                marginRight: '8px'
                              }"
                            />
                          </template>
                        </VListItem>
                      </template>

                      <template #selection="{ item }">
                        <div class="d-flex align-center">
                          <div
                            :style="{
                              backgroundColor: item.raw.color,
                              width: '16px',
                              height: '16px',
                              borderRadius: '4px',
                              marginRight: '8px'
                            }"
                          />
                          {{ item.raw.text }}
                        </div>
                      </template>
                    </VSelect>
                  </VCol>
                </VRow>
              </VCol>
             

              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="targetCount"
                    >
                      목표 등록수<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="projectDesc"
                      v-model="data.targetCount"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- 저장/취소 -->
              <VCol
                offset-md="3"
                cols="12"
                md="9"
                class="d-flex gap-4"
              >
                <VBtn
                  type="submit"
                  @click="saveProject"
                >
                  {{ $t('Save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  @click="$router.back()"
                >
                  {{ $t('Cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss" scoped>
:deep(.v-card.form-card) {
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(16, 24, 40, 6%);
}

.v-label.text-body-2.text-high-emphasis {
  display: inline-flex;
  align-items: center;
  color: #1f2937;
  font-weight: 600;
  gap: 6px;
}
:deep(.v-input) { --v-input-control-height: 44px; }
:deep(.v-radio-group), :deep(.v-switch) { margin-block-start: 4px; }
.d-flex.gap-4 { gap: 12px; }


.icon-20{ block-size:20px; inline-size:20px; }
body { background: #f6f8fb; }
</style>
